import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { KpiCard } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend } from "recharts";
import { FileDown, FileSpreadsheet, Milk, IndianRupee, Layers } from "lucide-react";
import { exportPDF, exportExcel } from "@/lib/exportUtils";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const monthStart = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

export default function Reports() {
  const { tenant } = useAuth();
  const { t } = useI18n();
  const [range, setRange] = useState({ from: monthStart(), to: today() });
  const [group, setGroup] = useState("day");
  const params = { date_from: range.from, date_to: range.to };

  const summary = useQuery({ queryKey: ["rep-summary", range, group], queryFn: async () => (await api.get("/reports/summary", { params: { ...params, group } })).data });
  const farmerWise = useQuery({ queryKey: ["rep-farmer", range], queryFn: async () => (await api.get("/reports/farmer-wise", { params })).data });
  const fatSnf = useQuery({ queryKey: ["rep-fatsnf", range], queryFn: async () => (await api.get("/reports/fat-snf", { params })).data });
  const payRep = useQuery({ queryKey: ["rep-pay", range], queryFn: async () => (await api.get("/reports/payments", { params })).data });
  const ledger = useQuery({ queryKey: ["rep-ledger", range], queryFn: async () => (await api.get("/reports/farmer-ledger", { params })).data });

  const fwCols = [
    { key: "farmer_code", header: "Code" }, { key: "farmer_name", header: "Farmer" },
    { key: "quantity", header: "Qty (L)" }, { key: "avg_fat", header: "Avg FAT" },
    { key: "avg_snf", header: "Avg SNF" }, { key: "amount", header: "Amount" },
  ];
  const ledgerCols = [
    { key: "farmer_code", header: "Code" }, { key: "farmer_name", header: "Farmer" },
    { key: "last_date", header: "Last Payment" },
    { key: "gross", header: "Gross" }, { key: "advance", header: "Advance" },
    { key: "feed_deduction", header: "Feed Ded." }, { key: "deduction", header: "Other Ded." },
    { key: "net", header: "Net Paid" }, { key: "feed_pending", header: "Pending Feed" },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <PageHeader
        title={t("rep.title")}
        subtitle={t("rep.subtitle")}
        breadcrumb={[t("crumb.center"), t("rep.title")]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input type="date" className="w-auto" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} data-testid="report-from" />
            <Input type="date" className="w-auto" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} data-testid="report-to" />
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <KpiCard label={t("rep.totalMilk")} value={`${summary.data?.totals?.quantity ?? 0} L`} icon={Milk} index={0} />
        <KpiCard label={t("rep.totalAmount")} value={money(summary.data?.totals?.amount)} icon={IndianRupee} index={1} accent="secondary" />
        <KpiCard label={t("rep.entries")} value={summary.data?.totals?.count ?? 0} icon={Layers} index={2} accent="warning" />
      </div>

      <Tabs defaultValue="summary">
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="summary" data-testid="tab-summary">{t("rep.summary")}</TabsTrigger>
          <TabsTrigger value="farmer" data-testid="tab-farmer">{t("rep.farmerWise")}</TabsTrigger>
          <TabsTrigger value="quality" data-testid="tab-quality">{t("rep.quality")}</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">{t("rep.payments")}</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">{t("rep.ledger")}</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">{t("rep.summary")}</CardTitle>
              <Select value={group} onValueChange={setGroup}>
                <SelectTrigger className="w-32" data-testid="summary-group"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="day">{t("rep.daily")}</SelectItem><SelectItem value="month">{t("rep.monthly")}</SelectItem></SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {summary.isLoading ? <Skeleton className="h-72" /> : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={summary.data.series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="period" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    <Legend />
                    <Bar dataKey="quantity" name="Litres" radius={[5, 5, 0, 0]} fill="hsl(var(--chart-1))" />
                    <Bar dataKey="amount" name="Amount ₹" radius={[5, 5, 0, 0]} fill="hsl(var(--chart-3))" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="farmer">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">{t("rep.farmerWise")}</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportExcel({ columns: fwCols, rows: farmerWise.data?.rows || [], filename: "farmer-wise" })} data-testid="export-farmer-excel"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
                <Button variant="outline" size="sm" onClick={() => exportPDF({ title: "Farmer-wise Report", subtitle: `${range.from} to ${range.to}`, columns: fwCols, rows: farmerWise.data?.rows || [], filename: "farmer-wise" })} data-testid="export-farmer-pdf"><FileDown className="mr-1.5 h-4 w-4" /> PDF</Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {farmerWise.isLoading ? <Skeleton className="m-4 h-64" /> : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Farmer</TableHead><TableHead className="text-right">Qty (L)</TableHead><TableHead className="text-right">Avg FAT</TableHead><TableHead className="text-right">Avg SNF</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {(farmerWise.data?.rows || []).map((r) => (
                        <TableRow key={r.farmer_id} className="hover:bg-muted/50">
                          <TableCell className="tnum">{r.farmer_code}</TableCell><TableCell className="font-medium">{r.farmer_name}</TableCell>
                          <TableCell className="text-right tnum">{r.quantity}</TableCell><TableCell className="text-right tnum">{r.avg_fat}</TableCell>
                          <TableCell className="text-right tnum">{r.avg_snf}</TableCell><TableCell className="text-right font-semibold tnum">{money(r.amount)}</TableCell>
                        </TableRow>
                      ))}
                      {(farmerWise.data?.rows || []).length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No data.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quality">
          <Card>
            <CardHeader><CardTitle className="font-heading text-lg">{t("rep.qualityTrend")}</CardTitle></CardHeader>
            <CardContent>
              {fatSnf.isLoading ? <Skeleton className="h-72" /> : (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={fatSnf.data.series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="period" tickFormatter={(d) => d.slice(5)} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} domain={["auto", "auto"]} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    <Legend />
                    <Line type="monotone" dataKey="avg_fat" name="Avg FAT" stroke="hsl(var(--chart-3))" strokeWidth={2.5} dot={false} />
                    <Line type="monotone" dataKey="avg_snf" name="Avg SNF" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card>
            <CardHeader><CardTitle className="font-heading text-lg">{t("rep.paymentReport")}</CardTitle></CardHeader>
            <CardContent>
              {payRep.isLoading ? <Skeleton className="h-64" /> : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                  {[["Gross", payRep.data.totals.gross], ["Advance", payRep.data.totals.advance], ["Deduction", payRep.data.totals.deduction], ["Bonus", payRep.data.totals.bonus], ["Net Paid", payRep.data.totals.net]].map(([k, v]) => (
                    <div key={k} className="rounded-lg border p-4"><p className="text-xs uppercase tracking-wide text-muted-foreground">{k}</p><p className="font-heading text-xl font-bold tnum">{money(v)}</p></div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ledger">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">{t("rep.ledgerReport")}</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportExcel({ columns: ledgerCols, rows: ledger.data?.rows || [], filename: "farmer-ledger" })} data-testid="export-ledger-excel"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
                <Button variant="outline" size="sm" onClick={() => exportPDF({ title: "Farmer Payment Ledger", subtitle: `${range.from} to ${range.to}`, columns: ledgerCols, rows: ledger.data?.rows || [], filename: "farmer-ledger" })} data-testid="export-ledger-pdf"><FileDown className="mr-1.5 h-4 w-4" /> PDF</Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {ledger.isLoading ? <Skeleton className="m-4 h-64" /> : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Code</TableHead><TableHead>Farmer</TableHead><TableHead>Last Payment</TableHead>
                      <TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Advance</TableHead>
                      <TableHead className="text-right">Feed Ded.</TableHead><TableHead className="text-right">Other Ded.</TableHead>
                      <TableHead className="text-right">Net Paid</TableHead><TableHead className="text-right">Pending Feed</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(ledger.data?.rows || []).map((r) => (
                        <TableRow key={r.farmer_id} className="hover:bg-muted/50" data-testid={`ledger-row-${r.farmer_id}`}>
                          <TableCell className="tnum">{r.farmer_code}</TableCell><TableCell className="font-medium">{r.farmer_name}</TableCell>
                          <TableCell className="text-xs">{r.last_date}</TableCell>
                          <TableCell className="text-right tnum">{money(r.gross)}</TableCell>
                          <TableCell className="text-right tnum text-destructive">{r.advance ? `-${money(r.advance)}` : money(0)}</TableCell>
                          <TableCell className="text-right tnum text-destructive">{r.feed_deduction ? `-${money(r.feed_deduction)}` : money(0)}</TableCell>
                          <TableCell className="text-right tnum text-destructive">{r.deduction ? `-${money(r.deduction)}` : money(0)}</TableCell>
                          <TableCell className="text-right font-semibold tnum">{money(r.net)}</TableCell>
                          <TableCell className="text-right tnum text-destructive">{r.feed_pending ? money(r.feed_pending) : money(0)}</TableCell>
                        </TableRow>
                      ))}
                      {(ledger.data?.rows || []).length === 0 && <TableRow><TableCell colSpan={9} className="py-8 text-center text-muted-foreground">No data.</TableCell></TableRow>}
                    </TableBody>
                    {(ledger.data?.rows || []).length > 0 && (
                      <TableBody className="border-t-2">
                        <TableRow className="font-semibold bg-muted/40" data-testid="ledger-totals-row">
                          <TableCell colSpan={3}>Total</TableCell>
                          <TableCell className="text-right tnum">{money(ledger.data.totals.gross)}</TableCell>
                          <TableCell className="text-right tnum text-destructive">-{money(ledger.data.totals.advance)}</TableCell>
                          <TableCell className="text-right tnum text-destructive">-{money(ledger.data.totals.feed_deduction)}</TableCell>
                          <TableCell className="text-right tnum text-destructive">-{money(ledger.data.totals.deduction)}</TableCell>
                          <TableCell className="text-right tnum">{money(ledger.data.totals.net)}</TableCell>
                          <TableCell className="text-right tnum text-destructive">{money(ledger.data.totals.feed_pending)}</TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}